import React from "react";
import { useIntl } from "react-intl";
import { alpha } from "@mui/material/styles";
import { AlertCircle, CheckCircle } from "@piiqtechnologies/svg-icons";
import { PeekAlert, PeekBox, PeekCircularProgress, PeekStack, PeekTypography } from "@piiqtechnologies/ui-components";
import { formatDateFromUTC } from "@piiqtechnologies/ui-lib";
import messages from "./messages.i18n";
const SubtopicsStatusIcon = ({ kind }) => (React.createElement(PeekBox, { bgcolor: "teal.200", borderRadius: "50%", width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
    React.createElement(PeekTypography, { variant: "h2", color: "teal.600", display: "flex" }, kind === "saved" ? (React.createElement(CheckCircle, null)) : kind === "firstRun" ? (React.createElement(PeekCircularProgress, { size: 20, color: "inherit" })) : (React.createElement(AlertCircle, null)))));
const SubtopicsStatusContent = ({ title, detail }) => (React.createElement(PeekStack, { direction: "column" },
    React.createElement(PeekTypography, { fontWeight: "fontWeightMedium" }, title),
    React.createElement(PeekTypography, null,
        "\u2022 ",
        detail)));
/**
 * One notice, three moments in the same story: the edit was saved, the report on screen still
 * predates it, or this is the report it first landed in. They share a shape because a reader
 * moving between Report Settings and the report itself is following one thread.
 */
export const SubtopicsStatusBanner = ({ state }) => {
    const intl = useIntl();
    if (!state)
        return null;
    // A run in flight is the one state with no date to give: what the reader wants to know is that
    // it is happening, not when it was scheduled for.
    const dated = state.kind === "running" || state.kind === "firstRun"
        ? undefined
        : new Date(state.kind === "applied" ? state.editedAt : state.nextRunAt);
    if (dated && Number.isNaN(dated.getTime()))
        return null;
    const shortDate = dated ? formatDateFromUTC(dated, "MMM d") : "";
    const ranAt = state.kind === "applied" ? new Date(state.ranAt) : undefined;
    const runDate = ranAt && !Number.isNaN(ranAt.getTime()) ? formatDateFromUTC(ranAt, "MMM d") : "";
    const longDate = dated ? formatDateFromUTC(dated, "MMMM d, yyyy") : "";
    const copy = {
        saved: {
            title: intl.formatMessage(messages.savedTitle, { shortDate }),
            detail: intl.formatMessage(messages.savedDetail, { longDate }),
        },
        pending: {
            title: intl.formatMessage(messages.pendingTitle, { shortDate }),
            detail: intl.formatMessage(messages.pendingDetail),
        },
        running: {
            title: intl.formatMessage(messages.runningTitle),
            detail: intl.formatMessage(messages.runningDetail),
        },
        firstRun: {
            title: intl.formatMessage(messages.firstRunTitle),
            detail: intl.formatMessage(messages.firstRunDetail),
        },
        applied: {
            title: intl.formatMessage(messages.appliedTitle),
            detail: intl.formatMessage(messages.appliedDetail, { runDate, shortDate }),
        },
    }[state.kind];
    return (React.createElement(PeekAlert, { role: "status", sx: (theme) => ({ backgroundColor: `${alpha(theme.palette.teal[100], 0.75)} !important` }) },
        React.createElement(PeekStack, { width: "100%", direction: "row", alignItems: "center", gap: 2 },
            React.createElement(SubtopicsStatusIcon, { kind: state.kind }),
            React.createElement(SubtopicsStatusContent, { title: copy.title, detail: copy.detail }))));
};
export default SubtopicsStatusBanner;
